/**
 * Immersive Layout Component
 * 
 * Full-screen 3D network background with floating panels for navigation and content.
 * Replaces the traditional sidebar-based layout for an immersive experience.
 * 
 * @version 1.0.0
 * @date January 13, 2026
 * @task T-S24-004
 * 
 * Features:
 * - 3D network renders as full-screen background
 * - Floating navigation panel (collapsible)
 * - Floating header bar with search, notifications, status
 * - All content rendered in FloatingWindow panels
 * - Panel positions persist via usePanelSync
 */

import { ReactNode, useState, useCallback, Suspense, lazy } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  LayoutDashboard,
  Network,
  AlertTriangle,
  FileText,
  Settings,
  Terminal,
  Swords,
  Bell,
  Search,
  Globe,
  Link2,
  Menu,
  X,
  ChevronRight,
  User,
  LogOut,
} from 'lucide-react';
import { useUIStore, selectNotifications } from '../../stores';
import { useSocketStatus } from '../../lib/socket';
import { RoleGate } from '../common/RoleGate';
import { OOBStatusIndicator } from '../ui/OutOfBandStatus';
import { useAuthStore } from '../../stores/authStore';
import { usePanelSync } from '../../hooks/usePanelSync';
import { LoadingScreen } from '../ui/LoadingScreen';
import type { NetworkNode, NetworkEdge } from '../3d/NetworkGraph3DEnhanced';

// Lazy load the 3D network component
const NetworkGraph3D = lazy(() => import('../3d/NetworkGraph3DEnhanced'));

// ============================================================================
// DEMO BACKGROUND DATA
// ============================================================================

/**
 * Generate demo nodes and edges for the background visualization.
 * This creates a visually appealing network without requiring API data.
 */
function generateBackgroundNetworkData() {
  const nodes: Array<{
    id: string;
    label: string;
    type: 'mdc' | 'ka' | 'ldc' | 'server' | 'workstation' | 'firewall' | 'gateway' | 'iot' | 'laptop' | 'mobile' | 'unknown';
    status: 'healthy' | 'warning' | 'critical' | 'offline';
    threatLevel: number;
    connections: string[];
    parentId?: string;
  }> = [];
  
  const edges: Array<{
    id: string;
    source: string;
    target: string;
    type: 'normal' | 'threat' | 'blocked';
    bidirectional: boolean;
  }> = [];
  
  // Create MDC (central node)
  nodes.push({
    id: 'mdc-1',
    label: 'MDC Primary',
    type: 'mdc',
    status: 'healthy',
    threatLevel: 0,
    connections: [],
  });
  
  // Create 3 LDCs
  const ldcIds = ['ldc-1', 'ldc-2', 'ldc-3'];
  ldcIds.forEach((ldcId, i) => {
    nodes.push({
      id: ldcId,
      label: `LDC ${['North', 'South', 'East'][i]}`,
      type: 'ldc',
      status: i === 1 ? 'warning' : 'healthy',
      threatLevel: i === 1 ? 1 : 0,
      connections: ['mdc-1'],
      parentId: 'mdc-1',
    });
    
    edges.push({
      id: `edge-mdc-${ldcId}`,
      source: 'mdc-1',
      target: ldcId,
      type: 'normal', // MDC-LDC connections are secure by default
      bidirectional: true,
    });
  });
  
  // Create devices under each LDC
  // Valid device types from NetworkNode (excluding 'threat' which is for indicators)
  type ValidDeviceType = 'server' | 'workstation' | 'gateway' | 'laptop' | 'iot' | 'firewall' | 'mobile';
  const deviceTypes: ValidDeviceType[] = ['server', 'workstation', 'gateway', 'laptop', 'iot'];
  const statuses: Array<'healthy' | 'warning' | 'critical' | 'offline'> = ['healthy', 'healthy', 'healthy', 'warning', 'critical'];
  
  ldcIds.forEach((ldcId, ldcIndex) => {
    const deviceCount = 5 + ldcIndex * 2; // 5, 7, 9 devices per LDC
    
    for (let i = 0; i < deviceCount; i++) {
      const deviceId = `${ldcId}-device-${i}`;
      const deviceType = deviceTypes[i % deviceTypes.length] ?? 'server';
      const status = statuses[Math.floor(Math.random() * statuses.length)] ?? 'healthy';
      
      nodes.push({
        id: deviceId,
        label: `${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)} ${i + 1}`,
        type: deviceType,
        status,
        threatLevel: status === 'critical' ? 3 : status === 'warning' ? 2 : 0,
        connections: [ldcId],
        parentId: ldcId,
      });
      
      edges.push({
        id: `edge-${ldcId}-${deviceId}`,
        source: ldcId,
        target: deviceId,
        type: status === 'critical' ? 'threat' : 'normal',
        bidirectional: false,
      });
      
      // Add some inter-device connections
      if (i > 0 && Math.random() > 0.6) {
        const prevDeviceId = `${ldcId}-device-${i - 1}`;
        edges.push({
          id: `edge-${deviceId}-${prevDeviceId}`,
          source: deviceId,
          target: prevDeviceId,
          type: 'normal',
          bidirectional: true,
        });
      }
    }
  });
  
  return { nodes, edges };
}

// Pre-generate background data (static, doesn't change)
const BACKGROUND_NETWORK_DATA = generateBackgroundNetworkData();

// ============================================================================
// TYPES
// ============================================================================

interface ImmersiveLayoutProps {
  children: ReactNode;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** Navigation panel width when expanded */
const NAV_WIDTH_EXPANDED = 280;
/** Navigation panel width when collapsed (icon only) */
const NAV_WIDTH_COLLAPSED = 64;

// Navigation items
const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', description: 'System overview' },
  { path: '/operator', icon: Shield, label: 'Operator', description: 'Operations center' },
  { path: '/network', icon: Network, label: '3D Network', description: 'Network topology' },
  { path: '/threats', icon: AlertTriangle, label: 'Threats', description: 'Active threats' },
  { path: '/policies', icon: FileText, label: 'Policies', description: 'Security policies' },
  { path: '/blockchain', icon: Link2, label: 'Blockchain', description: 'Policy validation' },
  { path: '/admin', icon: Settings, label: 'Admin', description: 'Administration' },
  { path: '/expert', icon: Terminal, label: 'Expert', description: 'Advanced tools' },
];

// ============================================================================
// FLOATING NAV PANEL
// ============================================================================

interface FloatingNavProps {
  isOpen: boolean;
  onToggle: () => void;
}

function FloatingNav({ isOpen, onToggle }: FloatingNavProps): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const socketStatus = useSocketStatus();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  const handleLogout = () => {
    logout();
    // Clear any remaining localStorage data
    localStorage.removeItem('raptor-auth-store');
    // Navigate to clear-session page which does thorough cleanup
    window.location.href = '/clear-session.html';
  };

  return (
    <motion.nav
      id="main-navigation"
      initial={false}
      animate={{
        width: isOpen ? NAV_WIDTH_EXPANDED : NAV_WIDTH_COLLAPSED,
        height: isOpen ? 'auto' : NAV_WIDTH_COLLAPSED,
      }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="fixed left-4 top-4 z-50 overflow-hidden rounded-2xl border border-slate-600/50 bg-slate-900/90 shadow-2xl backdrop-blur-xl"
      aria-label="Main navigation"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-700">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.15 }}
              >
                <h1 className="text-lg font-bold tracking-tight text-white">
                  Raptor Predator
                </h1>
                <p className="text-xs text-slate-400">Security Command</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <button
          onClick={onToggle}
          className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-700/50 hover:text-white"
          aria-label={isOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={isOpen}
          aria-controls="main-navigation"
        >
          {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Navigation (only when open) */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* War Room (special) */}
            <RoleGate minRole="operator" hideOnUnauthorized>
              <div className="px-3 pb-2">
                <NavLink
                  to="/war-room"
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all ${
                      isActive
                        ? 'bg-red-500/20 text-red-400 shadow-lg shadow-red-500/10'
                        : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                    }`
                  }
                >
                  <Swords className="h-5 w-5 shrink-0" />
                  <div className="flex-1">
                    <span className="text-sm font-medium">War Room</span>
                    <p className="text-xs text-slate-500">Active operations</p>
                  </div>
                  <ChevronRight className="h-4 w-4 opacity-50" />
                </NavLink>
              </div>
            </RoleGate>

            {/* Main Navigation */}
            <nav className="space-y-1 px-3 pb-3">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all ${
                      isActive
                        ? 'bg-cyan-500/20 text-cyan-400 shadow-lg shadow-cyan-500/10'
                        : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                    }`
                  }
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  <div className="flex-1">
                    <span className="text-sm font-medium">{item.label}</span>
                    <p className="text-xs text-slate-500">{item.description}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 opacity-50" />
                </NavLink>
              ))}
            </nav>

            {/* Divider */}
            <div className="mx-3 border-t border-slate-700/50" />

            {/* Connection Status */}
            <div className="p-3">
              <div
                className={`flex items-center gap-3 rounded-xl px-3 py-2 ${
                  socketStatus.connected
                    ? 'bg-emerald-500/10'
                    : socketStatus.reconnecting
                    ? 'bg-yellow-500/10'
                    : 'bg-slate-500/10'
                }`}
              >
                <div
                  className={`h-2 w-2 rounded-full ${
                    socketStatus.connected
                      ? 'bg-emerald-500 animate-pulse'
                      : socketStatus.reconnecting
                      ? 'bg-yellow-500 animate-pulse'
                      : 'bg-slate-500'
                  }`}
                />
                <span
                  className={`text-sm ${
                    socketStatus.connected
                      ? 'text-emerald-400'
                      : socketStatus.reconnecting
                      ? 'text-yellow-400'
                      : 'text-slate-400'
                  }`}
                >
                  {socketStatus.connected
                    ? 'Connected to MDC'
                    : socketStatus.reconnecting
                    ? 'Reconnecting...'
                    : 'Disconnected'}
                </span>
              </div>
            </div>

            {/* User Info */}
            {user && (
              <div className="border-t border-slate-700/50 p-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-700">
                    <User className="h-5 w-5 text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium text-white">{user.name}</p>
                    <p className="truncate text-xs text-slate-400">{user.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-500/20 hover:text-red-400"
                    title="Logout"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

// ============================================================================
// FLOATING HEADER BAR
// ============================================================================

function FloatingHeader(): JSX.Element {
  const location = useLocation();
  const notifications = useUIStore(selectNotifications);
  const toggleGlobalSearch = useUIStore((s) => s.toggleGlobalSearch);

  // Get current page title
  const pageTitle = location.pathname.split('/')[1] || 'Dashboard';
  const formattedTitle = pageTitle.charAt(0).toUpperCase() + pageTitle.slice(1).replace(/-/g, ' ');

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed right-4 top-4 z-40 flex items-center gap-3"
      aria-label="Page actions"
    >
      {/* Connection Status Badge - removed Demo Mode indicator per user request */}

      {/* Page Title */}
      <div className="rounded-xl border border-slate-600/50 bg-slate-900/90 px-4 py-2 backdrop-blur-xl">
        <span className="text-sm font-medium text-white">{formattedTitle}</span>
      </div>

      {/* Search */}
      <button
        onClick={toggleGlobalSearch}
        className="flex items-center gap-2 rounded-xl border border-slate-600/50 bg-slate-900/90 px-4 py-2 text-sm text-slate-400 backdrop-blur-xl transition-colors hover:border-slate-500 hover:text-white"
        aria-label="Open global search (⌘K)"
      >
        <Search className="h-4 w-4" />
        <span className="hidden md:inline">Search</span>
        <kbd className="hidden rounded border border-slate-600 bg-slate-700 px-1.5 py-0.5 text-xs md:inline">
          ⌘K
        </kbd>
      </button>

      {/* Notifications */}
      <button 
        className="relative rounded-xl border border-slate-600/50 bg-slate-900/90 p-2.5 text-slate-400 backdrop-blur-xl transition-colors hover:text-white"
        aria-label={`Notifications${notifications.length > 0 ? ` (${notifications.length} unread)` : ''}`}
      >
        <Bell className="h-5 w-5" />
        {notifications.length > 0 && (
          <span 
            className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white"
            aria-hidden="true"
          >
            {notifications.length > 9 ? '9+' : notifications.length}
          </span>
        )}
      </button>

      {/* OOB Status */}
      <OOBStatusIndicator compact />

      {/* MDC Status */}
      <div 
        className="flex items-center gap-2 rounded-xl border border-slate-600/50 bg-slate-900/90 px-3 py-2 backdrop-blur-xl"
        role="status"
        aria-label="MDC connection status: Connected"
      >
        <Globe className="h-4 w-4 text-cyan-400" aria-hidden="true" />
        <span className="text-xs text-slate-400" aria-hidden="true">MDC</span>
      </div>
    </motion.header>
  );
}

// ============================================================================
// MAIN LAYOUT
// ============================================================================

export function ImmersiveLayout({ children }: ImmersiveLayoutProps): JSX.Element {
  const [navOpen, setNavOpen] = useState(false);
  const toggleNav = useCallback(() => setNavOpen((prev) => !prev), []);
  
  // Initialize panel sync on layout mount
  usePanelSync();

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-slate-950">
      {/* 3D Network Background - DISABLED for dashboard readability */}
      {/* The 3D visualization should only appear on specific pages like /network or /operator */}
      {/* 
      <div className="absolute inset-0 z-0 overflow-hidden">
        <Suspense fallback={<LoadingScreen />}>
          <NetworkGraph3D
            nodes={BACKGROUND_NETWORK_DATA.nodes}
            edges={BACKGROUND_NETWORK_DATA.edges}
            className="h-full w-full"
            isBackground={true}
          />
        </Suspense>
      </div>
      */}

      {/* Gradient background for visual appeal without 3D distraction */}
      <div className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
      <div className="pointer-events-none absolute inset-0 z-0">
        {/* Subtle animated gradient effect */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-violet-500/5 rounded-full blur-3xl" />
      </div>

      {/* Floating Navigation Panel */}
      <FloatingNav isOpen={navOpen} onToggle={toggleNav} />

      {/* Floating Header Bar */}
      <FloatingHeader />

      {/* Main Content Area (floating panels render here) */}
      <main className="relative z-20 h-full w-full overflow-auto">
        {/* Content wrapper with solid background - uses full height for flex children */}
        <div className="h-full bg-slate-950/95 backdrop-blur-sm">
          {children}
        </div>
      </main>
    </div>
  );
}

export default ImmersiveLayout;
